import { useState, type VoidFunctionComponent } from 'react';
import type React from 'react';

import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import FallbackImage from '../../../generic/FallbackImage';
import Flex from '../../../generic/Flex';
import { icons } from '../../../generic/Icon/icons';
import { Icon } from '../../../generic/Icon/index';
import Loader from '../../../generic/Loader';
import Text from '../../../generic/Text';
import type { SBOMAnalysis, SBOMAnalysisRequiredFields } from '../../../../../shared/sbomSummary/sbomAnalysis';

import SBOMAnalysisSection from './SBOMAnalysisSection';
import useSBOMAnalysis from './useSBOMAnalysis';

import styles from './SBOMAnalysis.module.less';

export enum SBOMAnalysisCategory {
  FILE = 'file',
  REQUIRED_FIELDS = 'requiredFields',
  DEPENDENCY_SCAN = 'dependencyScan',
}

type SBOMAnalysisProps = {
  /**
   * The project locator.
   */
  projectLocator: string;
  /**
   * The revision ID.
   */
  revisionId: string;
  /**
   * The base URL for the revision for constructing links.
   */
  revisionBaseUrl: string;
  /**
   * Callback to trigger a rebuild.
   */
  onRebuild: (event: React.MouseEvent | React.KeyboardEvent) => Promise<void>;
  /**
   * Whether a build is in progress. If true, the SBOM analysis data will be displayed with no additional loading and empty state.
   */
  isBuildInProgress?: boolean;
  /**
   * Where the project has SBOM policy and SBOM analysis is enabled.
   */
  isSBOMAnalysisEnabled: boolean;
};

const NoDataFallback: VoidFunctionComponent<{
  onRebuild: (event: React.MouseEvent) => Promise<void>;
  isTriggeredRebuild: boolean;
}> = ({
  onRebuild,
  isTriggeredRebuild,
}) => {
  return (
    <FallbackImage src="no-data" alt="no-sbom-analysis-result" hasPadding>
      <Text.Heading>
        <Button
          type={BUTTON_TYPE.SECONDARY}
          size={BUTTON_SIZE.LARGE}
          onClick={onRebuild}
        >
          <Icon hasSpin={isTriggeredRebuild} icon={icons.refresh} /> Rebuild dependencies to view analysis
        </Button>
      </Text.Heading>
    </FallbackImage>
  );
};

const SBOMAnalysisSections: VoidFunctionComponent<{
  data: SBOMAnalysis;
  revisionBaseUrl: string;
  projectLocator: string;
  isSBOMAnalysisEnabled: boolean;
}> = ({
  data,
  revisionBaseUrl,
  projectLocator,
  isSBOMAnalysisEnabled,
}) => {
  return (
    <Flex direction="column" align="start" gap={24} className="width-full">
      <div className={styles.containerTitle} data-testid="sbom-analysis-title">
        Analysis
      </div>
      <SBOMAnalysisSection
        category={SBOMAnalysisCategory.FILE}
        data={data.sbomFile}
        testId="sbom-analysis-section-file"
      />

      <SBOMAnalysisSection
        category={SBOMAnalysisCategory.REQUIRED_FIELDS}
        data={data.requiredFields || {} as SBOMAnalysisRequiredFields}
        projectLocator={projectLocator}
        isSBOMAnalysisEnabled={isSBOMAnalysisEnabled}
        testId="sbom-analysis-section-required-fields"
      />

      <SBOMAnalysisSection
        category={SBOMAnalysisCategory.DEPENDENCY_SCAN}
        data={data.dependencyScan}
        revisionBaseUrl={revisionBaseUrl}
        testId="sbom-analysis-section-dependency-scan"
      />
    </Flex>
  );
};

/**
 * Display SBOM analysis data when there is no active build. This component will be used when users revisit the summary page for resolved revisions.
 */
const SBOMAnalysisComponentWithNoActiveBuild: VoidFunctionComponent<{
  onRebuild: (event: React.MouseEvent) => Promise<void>;
  isLoading: boolean;
  isFetched: boolean;
  data?: SBOMAnalysis;
  revisionBaseUrl: string;
  isTriggeredRebuild: boolean;
  projectLocator: string;
  isSBOMAnalysisEnabled: boolean;
}> = ({
  onRebuild,
  isLoading,
  isFetched,
  data,
  revisionBaseUrl,
  isTriggeredRebuild,
  projectLocator,
  isSBOMAnalysisEnabled,
}) => {
  if (isLoading && !isFetched) {
    return <Loader />;
  }

  if (!data) {
    return <NoDataFallback onRebuild={onRebuild} isTriggeredRebuild={isTriggeredRebuild} />;
  }

  return (<SBOMAnalysisSections
    data={data}
    revisionBaseUrl={revisionBaseUrl}
    projectLocator={projectLocator}
    isSBOMAnalysisEnabled={isSBOMAnalysisEnabled}
  />);
};

/**
 * Display SBOM analysis data when there is an active build. This component will be used immediately after the initial report or the build failed.
 */
const SBOMAnalysisComponentWithActiveBuild: VoidFunctionComponent<{
  data?: SBOMAnalysis;
  revisionBaseUrl: string;
  projectLocator: string;
  isSBOMAnalysisEnabled: boolean;
}> = ({
  data,
  revisionBaseUrl,
  projectLocator,
  isSBOMAnalysisEnabled,
}) => {
  if (!data) {
    return null;
  }

  return (<SBOMAnalysisSections
    data={data}
    revisionBaseUrl={revisionBaseUrl}
    projectLocator={projectLocator}
    isSBOMAnalysisEnabled={isSBOMAnalysisEnabled}
  />);
};

/**
 * Polls for SBOM analysis data and displays it when available. This component handles two scenarios:
 * 1. When isBuildInProgress is true, it will display the SBOM analysis data with no additional loading and empty state.
 *    The reason for this is that the SBOM analysis data typically comes in after the build is complete. We already have
 *    other components showing the progress of the build. Any additional loading state would be redundant.
 * 2. When isBuildInProgress is false, it will display the SBOM analysis data with a loading state and an empty state.
 *    The reason for this is that the SBOM analysis was fully stored in the database and can be fetched immediately.
 *    We show a loading state to indicate that we are fetching the data and an empty state if the data is not available.
 */
const SBOMAnalysisComponent: VoidFunctionComponent<SBOMAnalysisProps> = ({
  projectLocator,
  revisionId,
  revisionBaseUrl,
  onRebuild,
  isBuildInProgress,
  isSBOMAnalysisEnabled,
}) => {
  const revisionLocator = `${projectLocator}$${revisionId}`;
  const [isTriggeredRebuild, setTriggeredRebuild] = useState(false);
  const {
    data,
    isLoading,
    isFetched,
  } = useSBOMAnalysis(revisionLocator);

  if (isBuildInProgress) {
    return (
      <SBOMAnalysisComponentWithActiveBuild
        data={data}
        revisionBaseUrl={revisionBaseUrl}
        projectLocator={projectLocator}
        isSBOMAnalysisEnabled={isSBOMAnalysisEnabled}
      />
    );
  }

  return (
    <SBOMAnalysisComponentWithNoActiveBuild
      data={data}
      revisionBaseUrl={revisionBaseUrl}
      onRebuild={(e) => {
        setTriggeredRebuild(true);
        return onRebuild(e);
      }}
      isTriggeredRebuild={isTriggeredRebuild}
      isLoading={isLoading}
      isFetched={isFetched}
      projectLocator={projectLocator}
      isSBOMAnalysisEnabled={isSBOMAnalysisEnabled}
    />
  );
};

export default SBOMAnalysisComponent;
